"use client";

import { useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import {
  RightColumn,
  SectionHeader,
  SectionContent,
  Label,
  Value,
} from "./styles/SharedStyles";
import EditCulture from "../modals/EditCulture";
import EditButton from "@components/shared/EditButton";
import MarkdownList from "@components/shared/MarkdownList";
import { isHtmlEmpty, toEditorHtml } from "@lib/helpers/richText";
import { SummarySkeleton } from "./skeletons/SummarySkeleton";

const CultureSection = styled(Box)(({ theme }) => ({
  width: "85%",
  display: "flex",
  gap: "8px",
  marginBottom: theme.spacing(3),
  "&:last-child": {
    marginBottom: 0,
  },
}));

// Culture text is rich-text HTML from the editor, or plain text from a CV
// import; both are shown with the editor's spacing.
const richText = {
  "& p": { margin: "0 0 0.6em" },
  "& ul, & ol": { margin: "0 0 0.6em", paddingLeft: "1.4em" },
  "& p:last-child, & ul:last-child, & ol:last-child": { mb: 0 },
};

const FIELDS = [
  { key: "personality_summary", label: "Personality Summary:" },
  { key: "work_style", label: "Work Style:" },
  { key: "personal_story", label: "Personal Story:" },
] as const;

type CultureCandidate = Partial<Record<(typeof FIELDS)[number]["key"], string | null>> & Record<string, unknown>;

interface CultureProps {
  candidate: CultureCandidate;
  onUpdate: (candidate: Record<string, unknown>) => void;
  isImporting?: boolean;
}

const Culture = ({ candidate, onUpdate, isImporting }: CultureProps) => {
  const [openEditCulture, setOpenEditCulture] = useState(false);

  return (
    <>
      <Box>
        <SectionContent>
          <EditButton onEdit={() => setOpenEditCulture(true)} disabled={false} sx={{ top: 0, right: 0 }} />
          <Box>
            <RightColumn>
              <SectionHeader>
                <Typography variant="h2">Culture & Personality</Typography>
              </SectionHeader>

              {isImporting ? (
                <SummarySkeleton />
              ) : (
                // Shown as the editor shows it: older plain text (CV imports)
                // becomes the same paragraphs and lists as edited HTML.
                FIELDS.map(({ key, label }) => (
                  <CultureSection key={key}>
                    <Label>{label}</Label>
                    <Value sx={richText}>
                      {isHtmlEmpty(candidate[key] ?? "") ? "Not analyzed yet" : <MarkdownList content={toEditorHtml(candidate[key] ?? "")} />}
                    </Value>
                  </CultureSection>
                ))
              )}
            </RightColumn>
          </Box>
        </SectionContent>
      </Box>

      {openEditCulture && (
        <EditCulture
          open={openEditCulture}
          candidate={candidate}
          onClose={() => setOpenEditCulture(false)}
          onSave={onUpdate}
        />
      )}
    </>
  );
};

export default Culture;
